// src/contexts/GridContext.tsx
// Migrated to Supabase from Firebase Firestore
import React, { useEffect, useState, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useToast } from './ToastContext';
import { supabase } from '../supabase/config';
import { StaffAllocationGrid, ClassAllocation } from '../types/grid';
import { SubjectData } from '../utils/csvParser';
import { gridsService } from '../services/gridsService';
import { transformGridRow } from '../utils/transformGrid';
import { useGridsData } from './GridsDataContext';

interface GridContextType {
  grids: StaffAllocationGrid[];
  activeGrid: StaffAllocationGrid | null;
  loading: boolean;
  error: string | null;
  setActiveGrid: (gridId: string) => void;
  addGrid: (grid: Omit<StaffAllocationGrid, 'id'>) => Promise<void>;
  updateGrid: (gridId: string, updates: Partial<StaffAllocationGrid>) => Promise<void>;
  deleteGrid: (gridId: string) => Promise<void>;
  importGrid: (gridData: StaffAllocationGrid) => Promise<void>;
  importSubjectsToGrid: (subjects: SubjectData[] | string[]) => Promise<void>;
  exportGrid: (gridId: string) => StaffAllocationGrid | null;
}

const GridContext = createContext<GridContextType | undefined>(undefined);

export const GridProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const location = useLocation();
  // Shared grids data source (single fetch + subscription). Called unconditionally
  // so hook order stays stable regardless of the route branch below.
  const gridsData = useGridsData();

  // Disable this context on stabilized routes to prevent conflicts
  const stabilizedRoutes = ['/grid/editor', '/test/simple-grid-test'];
  const isStabilizedRoute = stabilizedRoutes.some(route => location.pathname.startsWith(route));
  
  if (isStabilizedRoute) {
    // But still provide grid management functions for creating/deleting grids
    const addGridForStabilized = async (gridData: Omit<StaffAllocationGrid, 'id'>) => {
      try {
        const cleanForSupabase = (obj: any): any => {
          if (obj === null || obj === undefined) return null;
          if (Array.isArray(obj)) {
            return obj.map(item => cleanForSupabase(item)).filter(item => item !== undefined);
          }
          if (typeof obj === 'object') {
            const cleaned: any = {};
            for (const [key, value] of Object.entries(obj)) {
              if (value !== undefined) {
                if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                  const cleanedValue = cleanForSupabase(value);
                  if (Object.keys(cleanedValue || {}).length > 0 || key === 'lineAssignments') {
                    cleaned[key] = cleanedValue || {};
                  }
                } else {
                  cleaned[key] = cleanForSupabase(value);
                }
              }
            }
            return cleaned;
          }
          return obj;
        };
        
        // Clean the grid data to remove undefined values
        const cleanedGridData = cleanForSupabase(gridData);
        
        const { data, error } = await supabase
          .from('grids')
          .insert({
            name: cleanedGridData.name,
            description: cleanedGridData.description || null,
            year: cleanedGridData.year || new Date().getFullYear(),
            term: cleanedGridData.term || null,
            created_by: 'system',
            is_active: true,
            grid_data: cleanedGridData,
            allocations: cleanedGridData.classes || [],
            metadata: {
              lines: cleanedGridData.lines || [],
              curriculumAreas: cleanedGridData.curriculumAreas || [],
              semesters: cleanedGridData.semesters || []
            }
          })
          .select()
          .single();
        
        if (error) {
          console.error('❌ GridContext: Error creating grid:', error);
          throw error;
        }
        
        return { id: data.id, ...gridData };
      } catch (error) {
        console.error('❌ GridContext: Error creating grid:', error);
        throw error;
      }
    };
    
    const deleteGridForStabilized = async (gridId: string) => {
      try {
        const { error } = await supabase
          .from('grids')
          .delete()
          .eq('id', gridId);
          
        if (error) {
          console.error('❌ GridContext: Error deleting grid:', error);
          throw error;
        }
        
      } catch (error) {
        console.error('❌ GridContext: Error deleting grid:', error);
        throw error;
      }
    };
    
    // Return a minimal context that won't interfere but provides grid management
    const minimalValue = {
      grids: [],
      activeGrid: null,
      loading: false,
      error: null,
      setActiveGrid: () => { },
      updateGrid: async () => { },
      addGrid: addGridForStabilized,
      deleteGrid: deleteGridForStabilized,
      importSubjectsToGrid: async () => { },
      importGrid: async () => { },
      exportGrid: () => null
    };
    return <GridContext.Provider value={minimalValue}>{children}</GridContext.Provider>;
  }

  const { addToast } = useToast();
  const [grids, setGrids] = useState<StaffAllocationGrid[]>([]);
  const [activeGrid, setActiveGridState] = useState<StaffAllocationGrid | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Derive grids from the shared grids data source (single fetch + subscription
  // owned by GridsDataProvider). Uses the canonical transform, ordered by name
  // (preserving gridsService.getAllGrids' `.order('name')`).
  useEffect(() => {
    try {
      const list = (gridsData.rawGrids || [])
        .map(row => transformGridRow(row))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setGrids(list);
      if (!activeGrid && list.length > 0) {
        setActiveGridState(list[0]);
      }
      setError(gridsData.error);
      setLoading(gridsData.loading);
    } catch (error) {
      console.error('❌ GridContext: Error deriving grids:', error);
      setError('Failed to fetch grid data');
      setLoading(false);
    }
    // activeGrid omitted: auto-select should run only while none is set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridsData.rawGrids, gridsData.loading, gridsData.error]);

  // Set active grid
  const setActiveGrid = (gridId: string) => {
    const grid = grids.find(g => g.id === gridId);
    if (grid) {
      setActiveGridState(grid);
    } else {
      console.error('❌ GridContext: Grid not found for ID:', gridId, 'Available grids:', grids.map(g => ({
        id: g.id,
        name: g.name
      })));
      setError(`Grid with ID ${gridId} not found`);
    }
  };

  // Helper function to clean data for Supabase
  const cleanForSupabase = (obj: any): any => {
    if (obj === null || obj === undefined) {
      return null;
    }
    if (Array.isArray(obj)) {
      return obj.map(cleanForSupabase).filter(item => item !== null && item !== undefined);
    }
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        // Handle empty objects (like lineAssignments: {})
        if (value !== undefined) {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            // For objects like lineAssignments, preserve empty objects but clean nested values
            const cleanedValue = cleanForSupabase(value);
            if (Object.keys(cleanedValue || {}).length > 0 || key === 'lineAssignments') {
              // Always preserve lineAssignments even if empty
              cleaned[key] = cleanedValue || {};
            }
          } else {
            cleaned[key] = cleanForSupabase(value);
          }
        }
      }
      return cleaned;
    }
    return obj;
  };

  // Add new grid
  const addGrid = async (gridData: Omit<StaffAllocationGrid, 'id'>) => {
    try {
      // Clean the grid data to remove undefined values
      const cleanedGridData = cleanForSupabase(gridData);
      
      const { data, error } = await supabase
        .from('grids')
        .insert({
          name: cleanedGridData.name,
          description: cleanedGridData.description || null,
          year: cleanedGridData.year || new Date().getFullYear(),
          term: cleanedGridData.term || null,
          created_by: 'system',
          is_active: true,
          grid_data: cleanedGridData,
          allocations: cleanedGridData.classes || [],
          metadata: {
            lines: cleanedGridData.lines || [],
            curriculumAreas: cleanedGridData.curriculumAreas || [],
            semesters: cleanedGridData.semesters || []
          }
        })
        .select()
        .single();

      if (error) {
        console.error('❌ GridContext: Error adding grid:', error);
        addToast('Failed to add grid', 'error');
        throw error;
      }

      // Create the new grid object
      const newGrid: StaffAllocationGrid = {
        id: data.id,
        ...gridData
      };

      // Update local state
      setGrids(prev => [...prev, newGrid]);
      setActiveGridState(newGrid);
      addToast('Grid added successfully', 'success');
    } catch (error) {
      console.error('❌ GridContext: Error adding grid:', error);
      addToast('Failed to add grid', 'error');
      throw error;
    }
  };

  // Enhanced update grid function with better error handling and validation
  const updateGrid = async (gridId: string, updates: Partial<StaffAllocationGrid>) => {
    // Validate gridId
    if (!gridId) {
      console.error('❌ GridContext: Invalid gridId provided');
      throw new Error('Invalid grid ID');
    }

    // Validate that grid exists
    const existingGrid = grids.find(g => g.id === gridId);
    if (!existingGrid) {
      console.error('❌ GridContext: Grid not found:', gridId);
      throw new Error(`Grid with ID ${gridId} not found`);
    }

    if (updates.classes) {
      // Validate and clean line assignments
      updates.classes = updates.classes.map(classItem => {
        // Ensure lineAssignments is an object and remove any undefined values
        const cleanedLineAssignments: Record<string, string | null> = {};
        if (classItem.lineAssignments) {
          Object.entries(classItem.lineAssignments).forEach(([lineId, teacherCode]) => {
            // Only include defined values, but allow null (for clearing assignments)
            if (teacherCode !== undefined) {
              cleanedLineAssignments[lineId] = teacherCode;
            }
          });
        }
        return {
          ...classItem,
          lineAssignments: cleanedLineAssignments,
          // Ensure other fields don't have undefined values
          studentCount: classItem.studentCount ?? null,
          maxStudents: classItem.maxStudents ?? 27,
          yearLevel: classItem.yearLevel ?? null
        };
      });
    }

    try {
      // Clean the updates more thoroughly
      const cleanedUpdates = cleanForSupabase(updates);

      // Additional validation for cleaned data
      if (!cleanedUpdates || Object.keys(cleanedUpdates).length === 0) {
        console.error('❌ GridContext: No valid updates after cleaning');
        throw new Error('No valid updates provided');
      }

      // Read the row's current jsonb so this update merges into it rather than
      // replacing it — grid_data also carries `classes`, which is not in scope
      // here and must survive.
      const { data: currentRow } = await supabase
        .from('grids')
        .select('grid_data')
        .eq('id', gridId)
        .single();
      const currentGridData = currentRow?.grid_data || {};

      const classes = cleanedUpdates.classes;
      const lines = cleanedUpdates.lines || existingGrid.lines;
      const curriculumAreas = cleanedUpdates.curriculumAreas || existingGrid.curriculumAreas;
      const semesters = cleanedUpdates.semesters || existingGrid.semesters;

      // `allocations` and `metadata` are NOT columns on pit.grids — writing them
      // made every update from this context fail with PGRST204 ("could not find
      // the 'allocations' column"), which took out the Import Grid page. The real
      // columns are `classes`, `curriculum_areas`, `grid_data` and `settings`.
      //
      // Classes go to BOTH `classes` and `grid_data.classes`: transformGridRow
      // prefers grid_data.classes when non-empty, so writing one without the
      // other makes them diverge and the grid shows a stale list. See
      // docs/wiki/Grid-Storage-And-Writes.md.
      const { error } = await supabase
        .from('grids')
        .update({
          name: cleanedUpdates.name,
          description: cleanedUpdates.description,
          ...(classes !== undefined && { classes }),
          ...(cleanedUpdates.curriculumAreas !== undefined && { curriculum_areas: curriculumAreas }),
          grid_data: {
            ...currentGridData,
            lines,
            curriculum_areas: curriculumAreas,
            study_periods: semesters,
            ...(classes !== undefined && { classes }),
            // Preserve the grid's own class length. This used to be hardcoded to
            // 70, silently rewriting grids that run 440-minute lines.
            default_class_minutes:
              currentGridData.default_class_minutes ?? existingGrid.defaultClassMinutes ?? 70
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', gridId);

      if (error) {
        console.error('❌ GridContext: Supabase update error:', error);
        throw error;
      }

      // Update active grid state more carefully
      if (activeGrid && activeGrid.id === gridId) {
        const updatedGrid = {
          ...activeGrid,
          ...updates
        };
        setActiveGridState(updatedGrid);
      }

      addToast('Grid updated successfully', 'success');
    } catch (error) {
      console.error('❌ GridContext: Error updating grid:', error);
      console.error('❌ GridContext: Update error details:', {
        gridId,
        updateKeys: Object.keys(updates),
        errorMessage: error instanceof Error ? error.message : 'Unknown error'
      });

      // More specific error messages
      let errorMessage = 'Failed to update grid';
      if (error instanceof Error) {
        if (error.message.includes('permission')) {
          errorMessage = 'Permission denied - please check your access';
        } else if (error.message.includes('not found')) {
          errorMessage = 'Grid not found - it may have been deleted';
        } else {
          errorMessage = `Update failed: ${error.message}`;
        }
      }
      addToast(errorMessage, 'error');
      throw error;
    }
  };

  // Delete grid
  const deleteGrid = async (gridId: string) => {
    try {
      const { error } = await supabase
        .from('grids')
        .delete()
        .eq('id', gridId);

      if (error) {
        console.error('❌ GridContext: Error deleting grid:', error);
        addToast('Failed to delete grid', 'error');
        throw error;
      }

      // If the active grid is deleted, set the first available grid as active
      if (activeGrid && activeGrid.id === gridId) {
        const remainingGrids = grids.filter(grid => grid.id !== gridId);
        setActiveGridState(remainingGrids.length > 0 ? remainingGrids[0] : null);
      }
      addToast('Grid deleted successfully', 'success');
    } catch (error) {
      console.error('❌ GridContext: Error deleting grid:', error);
      addToast('Failed to delete grid', 'error');
      throw error;
    }
  };

  // Import complete grid - creates new grid
  const importGrid = async (gridData: StaffAllocationGrid) => {
    try {
      // Clean the grid data to remove undefined values
      const cleanedGridData = cleanForSupabase({
        name: gridData.name,
        classes: gridData.classes,
        lines: gridData.lines,
        curriculumAreas: gridData.curriculumAreas,
        semesters: gridData.semesters
      });

      // Add the grid to Supabase
      const { data, error } = await supabase
        .from('grids')
        .insert({
          name: cleanedGridData.name,
          description: `Imported grid with ${gridData.classes.length} classes`,
          year: new Date().getFullYear(),
          term: null,
          created_by: 'system',
          is_active: true,
          grid_data: cleanedGridData,
          allocations: cleanedGridData.classes || [],
          metadata: {
            lines: cleanedGridData.lines || [],
            curriculumAreas: cleanedGridData.curriculumAreas || [],
            semesters: cleanedGridData.semesters || []
          }
        })
        .select()
        .single();

      if (error) {
        console.error('❌ GridContext: Error importing grid:', error);
        throw error;
      }

      // Create the new grid object with the Supabase ID
      const importedGrid: StaffAllocationGrid = {
        id: data.id,
        name: gridData.name,
        classes: gridData.classes,
        lines: gridData.lines,
        curriculumAreas: gridData.curriculumAreas,
        semesters: gridData.semesters
      };

      // Update local state
      setGrids(prev => [...prev, importedGrid]);
      setActiveGridState(importedGrid);
      addToast(`Grid "${gridData.name}" imported successfully with ${gridData.classes.length} classes`, 'success');
    } catch (err) {
      console.error('❌ GridContext: Error importing grid:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred during import';
      setError(errorMessage);
      addToast(`Import failed: ${errorMessage}`, 'error');
      throw err;
    }
  };

  // Import subjects to existing active grid
  const importSubjectsToGrid = async (subjects: SubjectData[] | string[]) => {
    if (!activeGrid) {
      throw new Error('No active grid selected. Please create or select a grid first.');
    }
    try {
      // Helper function to extract curriculum area from subject name
      const extractCurriculumArea = (subjectName: string): string => {
        const subject = subjectName.toUpperCase();
        if (subject.includes('ENGLISH')) return 'ENGLISH';
        if (subject.includes('MATHEMATICS') || subject.includes('MATHS')) return 'MATHEMATICS';
        if (subject.includes('SCIENCE') || subject.includes('BIOLOGY') || subject.includes('CHEMISTRY') || subject.includes('PHYSICS')) return 'SCIENCE';
        if (subject.includes('HISTORY') || subject.includes('GEOGRAPHY')) return 'HUMANITIES';
        if (subject.includes('ART') || subject.includes('VISUAL')) return 'ARTS';
        if (subject.includes('MUSIC')) return 'MUSIC';
        if (subject.includes('PE') || subject.includes('PHYSICAL') || subject.includes('SPORT')) return 'PHYSICAL EDUCATION';
        if (subject.includes('TECHNOLOGY') || subject.includes('DIGITAL')) return 'TECHNOLOGY';
        if (subject.includes('LANGUAGE') || subject.includes('ITALIAN') || subject.includes('CHINESE')) return 'LANGUAGES';
        if (subject.includes('BUSINESS') || subject.includes('ACCOUNTING')) return 'BUSINESS';
        if (subject.includes('RELIGION') || subject.includes('FAITH')) return 'RELIGION';
        if (subject.includes('AGRICULTURAL') || subject.includes('HORTICULTURE')) return 'AGRICULTURE';
        if (subject.includes('VET') || subject.includes('VOCATIONAL') || subject.includes('VCE VM') || subject.includes('VM ') || subject.includes('VCAL')) return 'VOCATIONAL MAJOR';
        if (subject.includes('ENGAGE') || subject.includes('DISCOVER') || subject.includes('FOCUS')) return 'FOCUS';
        return 'OTHER';
      };

      // Helper function to extract year level from subject name
      const extractYearLevel = (subjectName: string): number | null => {
        const subject = subjectName.toUpperCase();

        // Look for year level patterns
        const yearMatches = subject.match(/\b(\d{1,2})\b/);
        if (yearMatches) {
          const year = parseInt(yearMatches[1]);
          if (year >= 7 && year <= 12) {
            return year;
          }
        }
        return null;
      };

      // Create new ClassAllocation objects for each subject
      const newClasses: ClassAllocation[] = subjects.map((subject, index) => {
        // Handle both old string format and new SubjectData format
        let subjectName: string;
        let curriculumArea: string;
        let yearLevel: number | null;
        
        if (typeof subject === 'string') {
          // Legacy single-column format
          subjectName = subject;
          curriculumArea = extractCurriculumArea(subject);
          yearLevel = extractYearLevel(subject);
        } else {
          // New three-column format
          subjectName = subject.name;
          // Map faculty to curriculum area (use faculty as curriculum area if no mapping exists)
          const facultyToCurriculumMapping: Record<string, string> = {
            'Arts': 'ARTS',
            'English': 'ENGLISH', 
            'Humanities': 'HUMANITIES',
            'Languages': 'LANGUAGES',
            'Maths': 'MATHEMATICS',
            'Science': 'SCIENCE',
            'HPE': 'PHYSICAL EDUCATION',
            'PE': 'PHYSICAL EDUCATION',
            'Health & PE': 'PHYSICAL EDUCATION',
            'STEAM': 'TECHNOLOGY',
            'Technology': 'TECHNOLOGY',
            'Business': 'BUSINESS',
            'Religion': 'RELIGION',
            'Agriculture': 'AGRICULTURE',
            'VET': 'VET',
            'Vocational Major': 'VET',
            'Learning Diversity': 'LEARNING SUPPORT',
            'Retreat': 'RELIGION',
            'Oratory': 'PASTORAL CARE'
          };
          curriculumArea = facultyToCurriculumMapping[subject.faculty] || subject.faculty.toUpperCase();
          yearLevel = extractYearLevel(subject.name);
        }
        
        return {
          id: `class_${Date.now()}_${index}`,
          className: subjectName.trim(),
          subjectCode: typeof subject === 'object' ? subject.code : undefined, // Store the subject code if available
          curriculumArea,
          yearLevel: yearLevel,
          lineAssignments: {},
          maxStudents: 27,
          studentCount: null
        };
      });

      // Combine existing classes with new imported classes
      const updatedClasses = [...activeGrid.classes, ...newClasses];

      // Recalculate curriculum areas with new class counts
      const curriculumAreaCounts = new Map<string, number>();
      updatedClasses.forEach(classItem => {
        const area = classItem.curriculumArea;
        curriculumAreaCounts.set(area, (curriculumAreaCounts.get(area) || 0) + 1);
      });

      // Update curriculum areas with new counts and add any new areas
      const existingAreas = new Set(activeGrid.curriculumAreas.map(area => area.name));
      const updatedCurriculumAreas = [...activeGrid.curriculumAreas];

      // Update existing area counts
      updatedCurriculumAreas.forEach(area => {
        area.classCount = curriculumAreaCounts.get(area.name) || 0;
      });

      // Add new curriculum areas if they don't exist
      curriculumAreaCounts.forEach((count, areaName) => {
        if (!existingAreas.has(areaName)) {
          const getCurriculumAreaColor = (areaName: string): string => {
            // On-brand hexes drawn from the design tokens (src/styles/tokens.css).
            // Mirrors the token hexes documented in the COLORS array in
            // src/pages/reports/TeachingIntentionsReports.tsx.
            const colors: Record<string, string> = {
              'ENGLISH': '#B3261E',            // danger-solid
              'MATHEMATICS': '#2E6BAC',        // navy-500
              'SCIENCE': '#2E8B5E',            // ok-solid
              'HUMANITIES': '#C99213',         // warn-solid
              'ARTS': '#14447F',               // navy-700
              'MUSIC': '#1F5AA0',              // info-solid
              'PHYSICAL EDUCATION': '#D4AF37', // gold-400
              'TECHNOLOGY': '#5C86B5',         // navy-400
              'LANGUAGES': '#8A5800',          // gold-600
              'BUSINESS': '#146A41',           // ok-fg
              'RELIGION': '#00234B',           // navy-900
              'AGRICULTURE': '#5C6673',        // ink-500
              'VET': '#164679',                // info-fg
              'LEARNING SUPPORT': '#7A8493',   // ink-400
              'PASTORAL CARE': '#C99213',      // warn-solid
              'FOCUS': '#0A3266',              // navy-800
              'OTHER': '#7A8493'               // ink-400
            };
            return colors[areaName] || '#7A8493';
          };
          updatedCurriculumAreas.push({
            id: areaName.toLowerCase().replace(/\s+/g, '_'),
            name: areaName,
            color: getCurriculumAreaColor(areaName),
            classCount: count
          });
        }
      });

      // Update the grid with new classes and curriculum areas
      await updateGrid(activeGrid.id, {
        classes: updatedClasses,
        curriculumAreas: updatedCurriculumAreas
      });
      addToast(`${subjects.length} subjects imported successfully`, 'success');
    } catch (err) {
      console.error('❌ GridContext: Error importing subjects:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred during subject import';
      setError(errorMessage);
      addToast(`Subject import failed: ${errorMessage}`, 'error');
      throw err;
    }
  };

  // Export grid
  const exportGrid = (gridId: string): StaffAllocationGrid | null => {
    const grid = grids.find(g => g.id === gridId);
    if (!grid) {
      setError(`Grid with ID ${gridId} not found`);
      return null;
    }
    return grid;
  };

  const value = {
    grids,
    activeGrid,
    loading,
    error,
    setActiveGrid,
    addGrid,
    updateGrid,
    deleteGrid,
    importGrid,
    importSubjectsToGrid,
    exportGrid
  };

  return <GridContext.Provider value={value}>{children}</GridContext.Provider>;
};

export const useGrid = () => {
  const context = useContext(GridContext);
  if (context === undefined) {
    throw new Error('useGrid must be used within a GridProvider');
  }
  return context;
};